import { ChevronsUpDownIcon, XIcon } from "lucide-react";
import React from "react";
import {
  Autocomplete,
  AutocompleteContext,
  type AutocompleteProps,
  composeRenderProps,
  type InputProps,
  ListBoxContext,
  MenuTrigger,
  type MenuTriggerProps,
  Pressable,
  Provider,
  SelectStateContext,
  useFilter,
} from "react-aria-components";

import { Button, type ButtonProps } from "./button";
import { ListBox, type ListBoxProps } from "./list-box";
import { Popover, type PopoverProps } from "./popover";
import { SearchField, SearchInput } from "./search-field";
import { cn, composeTailwindRenderProps } from "./utils";

export type CommandBoxProps<T extends object> = MenuTriggerProps &
  Pick<AutocompleteProps, "filter"> &
  Pick<
    ListBoxProps<T>,
    "selectionMode" | "selectedKeys" | "onSelectionChange" | "renderEmptyState"
  >;

export function CommandBox<T extends object>({
  children,
  filter,
  selectedKeys,
  onSelectionChange,
  renderEmptyState,
  selectionMode,
  ...props
}: CommandBoxProps<T>) {
  const defaultFilter = useFilter({ sensitivity: "base" });
  return (
    <MenuTrigger {...props}>
      <Provider
        values={[
          [AutocompleteContext, { filter: filter ?? defaultFilter.contains }],
          [
            ListBoxContext,
            {
              selectedKeys,
              onSelectionChange,
              renderEmptyState,
              selectionMode,
            },
          ],
        ]}
      >
        {children}
      </Provider>
    </MenuTrigger>
  );
}

export function CommandBoxButton({
  hideIndicator,
  isClearable,
  ...props
}: ButtonProps & {
  hideIndicator?: boolean;
  isClearable?: boolean;
}) {
  const state = React.useContext(SelectStateContext);
  return (
    <Button
      variant="outline"
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        cn("relative", !hideIndicator && "pe-8"),
      )}
    >
      {composeRenderProps(props.children, (children) => {
        return (
          <>
            {children}

            {isClearable && state?.selectedKey ? (
              <Pressable
                onPress={() => state?.setSelectedKey(null)}
                isDisabled={props.isDisabled}
              >
                <XIcon
                  role="button"
                  tabIndex={0}
                  className="size-4 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:opacity-100 me-0! pointer-events-auto!"
                  aria-label="Clear"
                />
              </Pressable>
            ) : (
              <ChevronsUpDownIcon className="size-4 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground me-0!" />
            )}
          </>
        );
      })}
    </Button>
  );
}

export function CommandBoxPopover({ children, ...props }: PopoverProps) {
  return (
    <Popover
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        cn(
          "p-0 !max-h-96 min-w-(--trigger-width) flex flex-col overflow-hidden",
        ),
      )}
    >
      <Autocomplete>{children}</Autocomplete>
    </Popover>
  );
}

export function CommandBoxInput({ children, ...props }: InputProps) {
  return (
    <SearchField autoFocus className="border-b" aria-label="Search">
      <SearchInput
        placeholder="Search..."
        {...props}
        className={composeTailwindRenderProps(
          props.className,
          "border-none outline-none shadow-none rounded-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
        )}
      />
    </SearchField>
  );
}

export function CommandBoxList<T extends object>(
  props: Omit<
    ListBoxProps<T>,
    "selectedKeys" | "onSelectionChange" | "selectionMode"
  >,
) {
  return (
    <ListBox
      escapeKeyBehavior="none"
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        "outline-hidden p-1 overflow-auto flex-1 scroll-pb-1",
      )}
    />
  );
}
